import { useComputed } from "@suse-o11y/signal";
import type { FormUnit } from "@suse-o11y/signal/form";
import React from "react";

import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { Metric } from "~/models/metric";
import { filter } from "~/tools/filter";
import { flatMap } from "~/tools/flat-map";
import { isPresent } from "~/tools/is-present";
import { pipe } from "~/tools/pipe";
import { prop } from "~/tools/prop";
import { useTemplateVariables } from "~/tools/template-variables/use-template-variables";

import { PromQLInput } from "../../../../metrics/PromQLInput";
import { type InstantPromQLResult, queryInstantPromQL } from "../../../../queries/metrics/batch";
import { useQuery } from "../../../../queries/react-query";
import { generateVariableNames } from "../../../../routes/dashboards/dashboard/generate-variable-names";
import { LabelName } from "../_label-name";

import type { FormState } from "./_form-state";

function selectAllLabelNames(result: InstantPromQLResult) {
	return result.transform((vectors) => Metric.extractAllLabelNames(vectors.map(prop("metric")))).getOrElse([]);
}

export interface VariableFormProps {
	name: FormUnit<string, unknown, string>;
	state: FormState;
}

export const VariableForm: React.FC<VariableFormProps> = ({ state, name }) => {
	const templateVariables = useTemplateVariables();

	const expression = useComputed(
		(monitor) => {
			const template = state.fields.expression.getOutput(monitor);

			if (!template) {
				return "";
			}

			const omittedVariableNames = pipe(
				[name.getInitial(monitor), name.getOutput(monitor)],
				filter(isPresent),
				flatMap(generateVariableNames),
			);

			return (
				template.interpolate(templateVariables.values, {
					// do not send partially interpolated templates
					whenVariableMissing: undefined,
					// do not self-reference the variable being queried
					omit: omittedVariableNames,
				}) ?? ""
			);
		},
		[name, state, templateVariables],
	);

	const { data: suggestions = [] } = useQuery({
		enabled: expression.length > 0,
		keepPreviousData: true,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		select: selectAllLabelNames,

		...queryInstantPromQL({ step: undefined, query: expression }),
	});

	return (
		<>
			<FormField className="items-stretch" state={state.fields.expression}>
				<FormLabel>PromQL Expression *</FormLabel>

				{(monitor, field) => (
					<PromQLInput onChange={(value) => field.setInput(value)} value={field.getInput(monitor)} />
				)}
			</FormField>

			<LabelName state={state.fields.labelName} suggestions={suggestions} />
		</>
	);
};
